// R116 #1 "Welcome home" — ONE shell for every sign-in screen (login, forgot,
// set-password for invites + resets, the expired-link card, the shared-crew
// "Who's working on this device?" prompt): the stage (authFx), one card in the
// Calendar stay-panel idiom (soft card, small uppercase labels, ONE blue
// full-width primary, soft notices, muted helper text) and the drawn brand
// mark. Field behaviour lives here once: Show password, Caps Lock hint,
// persistent announced errors, the busy spinner, friendly Supabase error copy.
import './auth.css';
import { brandMarkSvg } from './authArt';
import { mountAuthFx } from './authFx';

export const esc = (s: unknown): string => String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));

export type NoticeTone = 'info' | 'warn' | 'ok';
export type Notice = { tone: NoticeTone; text: string; id?: string };

const NOTE_ICON: Record<NoticeTone, string> = {
  info: '<path d="M12 16v-4M12 8h.01"/><circle cx="12" cy="12" r="9"/>',
  warn: '<path d="M12 9v4M12 17h.01"/><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z"/>',
  ok: '<path d="M20 6 9 17l-5-5"/>',
};
const svgIcon = (paths: string, size = 16) => `<svg class="au-ic" viewBox="0 0 24 24" width="${size}" height="${size}" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">${paths}</svg>`;

/** A soft notice (session expired, why a login was turned away, a deep link, success). Persistent. */
export function noticeHtml(n: Notice): string {
  return `<div class="au-note au-note-${n.tone}" role="status"${n.id ? ` id="${esc(n.id)}"` : ''}>${svgIcon(NOTE_ICON[n.tone])}<span>${esc(n.text)}</span></div>`;
}

/** "Good morning" … by the local clock. */
export function greeting(now: Date = new Date()): string {
  const h = now.getHours();
  if (h >= 5 && h < 12) return 'Good morning';
  if (h >= 12 && h < 17) return 'Good afternoon';
  if (h >= 17 && h < 22) return 'Good evening';
  return 'Hello, night owl';
}

export type CardOptions = {
  screen: string;              // data-au-screen (login | forgot | set-password | expired | who)
  hello: string;               // the line under the brand (may hold <b>)
  helper?: string;             // muted helper text under it
  notices?: Notice[];
  body: string;                // the form / actions
  foot?: string;               // the hairline-separated footer line
  compactBrand?: boolean;      // the who's-working prompt: mark + one line only
};

/** The card in its full-viewport, transparent wrap (Skeptic D1c: relative + z-index 1 over the z-index 0 stage). */
export function authCardHtml(o: CardOptions): string {
  const brand = o.compactBrand
    ? `<header class="au-brand au-brand-compact"><span class="au-mark" aria-hidden="true">${brandMarkSvg(44)}</span></header>`
    : `<header class="au-brand">
        <span class="au-mark" aria-hidden="true">${brandMarkSvg(56)}</span>
        <h1 class="au-title" id="au-title">West Coast Homestays</h1>
        <div class="au-sub">Operations Manager</div>
        <div class="au-by">by Surge</div>
      </header>`;
  return `<div class="au-wrap" data-au-screen="${esc(o.screen)}">
    <main class="au-card"${o.compactBrand ? '' : ' aria-labelledby="au-title"'}>
      ${brand}
      <p class="au-hello">${o.hello}</p>
      ${o.helper ? `<p class="au-helper">${o.helper}</p>` : ''}
      <div class="au-notes">${(o.notices || []).map(noticeHtml).join('')}</div>
      ${o.body}
      ${o.foot ? `<footer class="au-foot">${o.foot}</footer>` : ''}
    </main>
  </div>`;
}

/** Paint a screen into the container and make sure the stage is up (idempotent). */
export function mountAuthScreen(container: HTMLElement, html: string): HTMLElement {
  mountAuthFx();
  container.innerHTML = html;
  return container.querySelector('.au-card') as HTMLElement;
}

/** The boot splash (client/index.html) ends where the app takes over. */
export function endBootSplash(): void {
  document.documentElement.classList.remove('au-on', 'au-dusk', 'au-paused', 'au-fx-fine', 'au-celebrate');
}

// ── field kit ────────────────────────────────────────────────────────────

/** A labelled field: label for=, name, autocomplete; the input itself never animates. */
export function fieldHtml(f: { id: string; label: string; type: string; name: string; autocomplete: string; placeholder?: string; extra?: string; reveal?: boolean; after?: string }): string {
  return `<div class="au-field${f.reveal ? ' has-reveal' : ''}">
      <label class="au-label" for="${f.id}">${esc(f.label)}</label>
      <input class="au-input" id="${f.id}" name="${f.name}" type="${f.type}" autocomplete="${f.autocomplete}"${f.placeholder ? ` placeholder="${esc(f.placeholder)}"` : ''} required ${f.extra || ''}>
      ${f.reveal ? `<button type="button" class="au-reveal" data-au-reveal="${f.id}" aria-controls="${f.id}" aria-pressed="false">Show</button>` : ''}
      ${f.after || ''}
    </div>`;
}

export const EMAIL_ATTRS = 'inputmode="email" autocapitalize="off" autocorrect="off" spellcheck="false"';

export function capsHintHtml(id: string): string {
  return `<div class="au-caps" id="${id}" aria-live="polite" hidden>${svgIcon('<path d="M12 4 4 12h4v6h8v-6h4z"/>', 14)}<span>Caps Lock is on</span></div>`;
}

export function alertHtml(id: string): string {
  return `<div class="au-alert" id="${id}" role="alert" hidden>${svgIcon('<circle cx="12" cy="12" r="9"/><path d="M12 8v4M12 16h.01"/>')}<span class="au-alert-t"></span></div>`;
}

export function submitHtml(id: string, label: string): string {
  return `<button type="submit" class="au-btn" id="${id}" data-au-magnet><span class="au-btn-t">${esc(label)}</span></button>`;
}

/** Show password: toggles type=text/password (the button is in the label row, never inside the input — Jesse's password-manager icon lives there). */
export function bindReveal(scope: HTMLElement, ids?: string[]): () => void {
  const btn = scope.querySelector<HTMLButtonElement>('[data-au-reveal]');
  if (!btn) return () => {};
  const targets = (ids || [btn.dataset.auReveal as string]).map(id => scope.querySelector<HTMLInputElement>(`#${id}`)).filter(Boolean) as HTMLInputElement[];
  const set = (show: boolean) => {
    targets.forEach(t => { t.type = show ? 'text' : 'password'; });
    btn.setAttribute('aria-pressed', String(show));
    btn.textContent = show ? 'Hide' : 'Show';
  };
  btn.addEventListener('click', () => set(btn.getAttribute('aria-pressed') !== 'true'));
  return () => set(false);   // call before submit: password managers only offer to save a type=password field (Skeptic D6)
}

/** Caps Lock hint under a password field. */
export function bindCaps(input: HTMLInputElement | null, hint: HTMLElement | null): void {
  if (!input || !hint) return;
  const upd = (e: KeyboardEvent) => { if (typeof e.getModifierState === 'function') hint.hidden = !e.getModifierState('CapsLock'); };
  input.addEventListener('keydown', upd);
  input.addEventListener('keyup', upd);
  input.addEventListener('blur', () => { hint.hidden = true; });
}

/** A persistent, announced error: stays until the user edits a field (never a 5 s auto-hide). */
export function showAlert(alertEl: HTMLElement | null, text: string, fields: Array<HTMLInputElement | null> = []): void {
  if (!alertEl) return;
  const t = alertEl.querySelector('.au-alert-t');
  if (t) t.textContent = text; else alertEl.textContent = text;
  alertEl.hidden = false;
  fields.forEach(f => f && f.setAttribute('aria-invalid', 'true'));
  const card = alertEl.closest('.au-card');
  const mark = card?.querySelector('.au-mk');
  if (mark) { mark.classList.remove('is-nope'); void (mark as unknown as HTMLElement).getBoundingClientRect(); mark.classList.add('is-nope'); }
}
export function clearAlert(alertEl: HTMLElement | null, fields: Array<HTMLInputElement | null> = []): void {
  if (alertEl) alertEl.hidden = true;
  fields.forEach(f => f && f.removeAttribute('aria-invalid'));
}
/** Hide the alert on the next edit of any of these fields. */
export function clearAlertOnInput(alertEl: HTMLElement | null, fields: Array<HTMLInputElement | null>): void {
  fields.forEach(f => f && f.addEventListener('input', () => { if (alertEl && !alertEl.hidden) clearAlert(alertEl, fields); }));
}

/** The busy / idle / done states of the one primary button (spinner, aria-busy, centred label). */
export function setBusy(btn: HTMLButtonElement | null, busy: boolean, label: string): void {
  if (!btn) return;
  btn.disabled = busy;
  if (busy) btn.setAttribute('aria-busy', 'true'); else btn.removeAttribute('aria-busy');
  btn.classList.remove('is-done');
  btn.innerHTML = `${busy ? '<span class="au-spin" aria-hidden="true"></span>' : ''}<span class="au-btn-t">${esc(label)}</span>`;
  if (!busy) { btn.style.removeProperty('--mx'); btn.style.removeProperty('--my'); }
}
export function setDone(btn: HTMLButtonElement | null, label: string): void {
  if (!btn) return;
  btn.disabled = true;
  btn.removeAttribute('aria-busy');
  btn.classList.add('is-done');
  btn.innerHTML = `${svgIcon('<path d="M20 6 9 17l-5-5"/>', 18)}<span class="au-btn-t">${esc(label)}</span>`;
}

/** The house mark reacts: window lit / door open / busy. */
export function markState(card: HTMLElement | null, s: { lit?: boolean; open?: boolean; busy?: boolean }): void {
  const m = card?.querySelector('.au-mark');
  if (!m) return;
  if (s.lit !== undefined) m.classList.toggle('is-lit', s.lit);
  if (s.open !== undefined) m.classList.toggle('is-open', s.open);
  if (s.busy !== undefined) m.classList.toggle('is-busy', s.busy);
}

/** The window lights once every listed field has a value (typed or autofilled). */
export function bindMarkLight(card: HTMLElement | null, fields: Array<HTMLInputElement | null>): void {
  const list = fields.filter(Boolean) as HTMLInputElement[];
  const filled = new Set<HTMLInputElement>();
  const upd = () => markState(card, { lit: list.every(f => f.value.trim() !== '' || filled.has(f)) });
  list.forEach(f => {
    f.addEventListener('input', () => { filled.delete(f); upd(); });
    // Chrome autofill exposes no value before a user gesture; auth.css runs a 1 ms animation on :-webkit-autofill
    f.addEventListener('animationstart', (e) => { if ((e as AnimationEvent).animationName === 'au-autofill') { filled.add(f); upd(); } });
  });
  upd();
}

// ── Supabase error copy (by error.code — Skeptic D10) ─────────────────────
export function authErrorCopy(err: any, ctx: 'login' | 'reset' | 'update' = 'login'): string {
  const code = String(err?.code || '');
  const name = String(err?.name || '');
  const status = Number(err?.status || 0);
  const msg = String(err?.message || '');
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return "You're offline. Reconnect, then try again.";
  if (name === 'AuthRetryableFetchError' || /failed to fetch|networkerror|load failed|network request failed/i.test(msg)) {
    return status >= 500 ? 'The sign-in service is having a moment. Try again in a minute.' : "Couldn't reach the sign-in service. Check your connection, then try again.";
  }
  switch (code) {
    case 'invalid_credentials': return "That email and password don't match. Try again, or reset your password.";
    case 'email_not_confirmed': return "This login hasn't been confirmed yet. Open the invite email, or ask your admin to resend it.";
    case 'user_banned': return 'This login is switched off. Ask your admin.';
    case 'over_request_rate_limit':
    case 'over_email_send_rate_limit': return ctx === 'reset' ? 'Too many reset emails in a row. Wait a few minutes, then try again.' : 'Too many tries in a row. Wait a minute, then try again.';
    case 'same_password': return "That's your current password. Choose a new one.";
    case 'weak_password': return 'That password is too easy to guess. Use at least 8 characters with letters and numbers.';
    case 'email_address_invalid': return "That doesn't look like an email address.";
    case 'session_not_found':
    case 'session_expired':
    case 'refresh_token_not_found':
    case 'otp_expired': return 'This link has timed out. Ask your admin to resend your invite.';
  }
  if (name === 'AuthSessionMissingError') return ctx === 'update' ? 'This link has timed out. Ask your admin to resend your invite.' : 'Your session ended. Sign in again.';
  if (code === 'validation_failed' && ctx !== 'update') return 'Check the email address and try again.';
  return msg || 'Something went wrong. Try again.';
}

// ── notices handed over through sessionStorage ────────────────────────────
/** The hotfix's 'authNotice' (why a login was turned away) + 'authNoticeOk' (password saved). Read once. */
export function takeAuthNotices(): Notice[] {
  const out: Notice[] = [];
  try {
    const warn = sessionStorage.getItem('authNotice');
    const ok = sessionStorage.getItem('authNoticeOk');
    sessionStorage.removeItem('authNotice');
    sessionStorage.removeItem('authNoticeOk');
    if (ok) out.push({ tone: 'ok', text: ok, id: 'auth-notice-ok' });
    if (warn) out.push({ tone: 'warn', text: warn, id: 'auth-notice' });
  } catch { /* private mode */ }
  return out;
}

/** A Slack "Open" link waiting for sign-in (read-only — App.render consumes it). */
export function deepLinkNotice(): Notice | null {
  let raw = '';
  try { raw = sessionStorage.getItem('deep_open') || ''; } catch { return null; }
  const m = /^(wo|task|issue|stay):/.exec(raw);
  if (!m) return null;
  const what = ({ wo: 'work order', task: 'task', issue: 'issue', stay: 'stay' } as Record<string, string>)[m[1]];
  return { tone: 'info', text: `Sign in to open the ${what} from your link.`, id: 'auth-deep' };
}
